import {
  ContentIndex,
  LessonBundle,
  type ContentIndex as CI,
  type LessonBundle as LB,
} from '@ezishizi/content-schema';

let indexPromise: Promise<CI> | null = null;
const bundles = new Map<string, Promise<LB>>();

export function loadIndex(): Promise<CI> {
  if (indexPromise) return indexPromise;
  const own: Promise<CI> = fetch('/content/index.json')
    .then((r) => {
      if (!r.ok) throw new Error(`failed to fetch content index: ${r.status}`);
      return r.json();
    })
    // The content pipeline already runs ContentIndex.parse on this file when it's
    // built (packages/content-pipeline/src/build.ts), and the schema has no
    // transforms, so re-parsing on every load just burns CPU on the critical path.
    // Re-validate in dev only, to catch schema drift early; trust the file in prod.
    .then((j) => (import.meta.env.DEV ? ContentIndex.parse(j) : (j as CI)))
    .catch((err) => {
      if (indexPromise === own) indexPromise = null;
      throw err;
    });
  indexPromise = own;
  return own;
}

export async function loadLesson(lessonId: string): Promise<LB> {
  let p = bundles.get(lessonId);
  if (!p) {
    const own: Promise<LB> = loadIndex()
      .then(async (idx) => {
        const entry = idx.lessons.find((l) => l.id === lessonId);
        if (!entry?.url) throw new Error(`lesson ${lessonId} is not authored yet`);
        const r = await fetch(entry.url);
        if (!r.ok) throw new Error(`failed to fetch lesson ${lessonId}: ${r.status}`);
        const j = await r.json();
        // Same reasoning as loadIndex: the bundle on disk is already the parsed
        // output, so only re-validate in dev.
        return import.meta.env.DEV ? LessonBundle.parse(j) : (j as LB);
      })
      .catch((err) => {
        // Only evict this attempt: a retry may already have replaced it.
        if (bundles.get(lessonId) === own) bundles.delete(lessonId);
        throw err;
      });
    p = own;
    bundles.set(lessonId, p);
  }
  return p;
}
